import { Text } from '@mantine/core'

import { useTranslation } from '@flecto/translations'
import { type CoreInquiry, type CoreProduct } from '@flecto/types'
import {
  AvatarProductsGroup,
  Badge,
  DataTable,
  type DataTableColumn,
  type DataTableProps,
} from '@flecto/ui'

import { BadgeInquiryStatus } from '../BadgeInquiryStatus/BadgeInquiryStatus'
import { CellActions } from './CellActions'
import { CellDates } from './CellDates'
import { CellOwner } from './CellOwner'

export type TableInquiriesProps = Omit<
  DataTableProps<CoreInquiry>,
  'columns'
> & {
  inquiries?: CoreInquiry[]
}

export function TableInquiries({ inquiries, ...props }: TableInquiriesProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'inquiries' })

  const columns: DataTableColumn<CoreInquiry>[] = [
    {
      accessor: 'id',
      title: 'ID',
      width: 70,
      render: (row) => {
        return <Text color='dimmed'>{row.internal_id}</Text>
      },
    },
    {
      accessor: 'full_name',
      title: t('table.sentby'),
      width: 200,
      render: (row) => <CellOwner inquiry={row} />,
    },
    {
      accessor: 'start_at',
      title: t('table.dates'),
      width: 360,
      render: (row) => <CellDates inquiry={row} />,
    },
    {
      accessor: 'status',
      title: t('table.status'),
      width: 130,
      render: (row) => {
        return <BadgeInquiryStatus inquiry={row} />
      },
    },
    {
      accessor: 'flexible_dates',
      width: 150,
      title: t('table.flexible_dates'),
      render: (row) => {
        return row.flexible_dates ? (
          <Badge color='green'>{t('yes')}</Badge>
        ) : (
          <Badge color='red'>{t('no')}</Badge>
        )
      },
    },
    {
      accessor: 'type',
      title: t('table.items'),
      render: (row) => {
        const products: CoreProduct[] = []

        row.inquiry_products?.forEach((ip) => {
          if (ip.product) {
            const itemWithCorrectQuantity = {
              ...ip.product,
              quantity: ip.quantity,
            }

            products.push(itemWithCorrectQuantity)
          }
        })

        return <AvatarProductsGroup products={products} />
      },
    },
    {
      accessor: 'actions',
      title: '',
      render: (row) => <CellActions inquiry={row} />,
    },
  ]

  return <DataTable {...props} columns={columns} records={inquiries} />
}
